'use client';

import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import IconButton from '@alltrails/denali/components/IconButton';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import { getFormattedDistance } from '@alltrails/data-formatting/distanceFormatters';
import { minutesToFormattedTime } from '@alltrails/data-formatting/dateTimeFormatters';
import { ratingToFormattedRating } from '@alltrails/data-formatting/numberFormatters';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import { useAppSelector } from '../../redux';
import { getTrailDifficultyLabelFromNumber } from '../../utils/trailDifficultyHelpers';
import styles from './styles/styles.module.scss';
import { TrailListItemData } from './types/types';

type TrailListItemProps = {
  index: number;
  trail: TrailListItemData;
  variant?: 'primary' | 'inverse';
  hideDesktopArrow?: boolean;
  hideTrailNumber?: boolean;
  setHovered: () => void;
  removeHovered: () => void;
};

const TrailListItem = ({
  index,
  trail,
  variant = 'primary',
  hideDesktopArrow = false,
  hideTrailNumber = false,
  setHovered,
  removeHovered
}: TrailListItemProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const intl = useIntl();
  const displayMetric = useAppSelector(state => state.context.displayMetric);

  const labelNumber = index + 1;
  const { name, slug, avgRating, difficulty, length, durationMinutes = 0, photoId, photoHash, locationName } = trail;

  const arrowVariant = useMemo(() => {
    return variant === 'primary' ? 'elevated' : 'default';
  }, [variant]);

  const photoUrl = useMemo(() => {
    if (!photoId || !photoHash) {
      return null;
    }
    return getPhotoUrl({ isOptimized: true, photo: { id: photoId, photoHash }, size: '500x500' });
  }, [photoId, photoHash]);

  const localizedRating = useMemo(() => {
    if (!avgRating) {
      return null;
    }
    return ratingToFormattedRating(avgRating, intl);
  }, [avgRating, intl]);

  const difficultyText = getTrailDifficultyLabelFromNumber(intl, difficulty);

  return (
    <li className={styles.item} onMouseEnter={setHovered} onMouseLeave={removeHovered}>
      <a className={styles.link} href={wrapUrlSafe(`/trail/${slug}`, languageRegionCode)} target="_blank">
        {!hideTrailNumber && <div className={styles.mobileNumberColumn}>{labelNumber}</div>}
        {photoUrl && <Image src={photoUrl} className={styles.image} alt={name} width={248} height={140} />}
        <div className={styles.textContent}>
          <h3 className={styles.heading}>
            {!hideTrailNumber && (
              <span className={styles.desktopNumberPrefix}>
                <FormattedMessage
                  defaultMessage="#{labelNumber} - "
                  values={{
                    labelNumber
                  }}
                />
              </span>
            )}
            {name}
          </h3>
          {locationName && <div className={styles.locationName}>{locationName}</div>}
          <div className={styles.stats}>
            {!!avgRating && <StarFilled color="currentColor" size="xs" className={styles.star} />}
            {!!avgRating && <span className={styles.ratingSpacing}>{localizedRating}</span>}
            {!!difficulty && (
              <div>
                {!!avgRating && <InfoDividerDot />}
                <span>{difficultyText}</span>
              </div>
            )}
            {!!length && (
              <div className={styles.distance}>
                {(!!difficulty || !!avgRating) && <InfoDividerDot />}
                <span>{getFormattedDistance(intl, length, displayMetric)}</span>
              </div>
            )}
            {!!durationMinutes && (
              <div className={styles.duration}>
                <InfoDividerDot className={styles.dividerDot} />
                {minutesToFormattedTime({ intl, totalMinutes: durationMinutes, isEstimate: true })}
              </div>
            )}
          </div>
        </div>
        {!hideDesktopArrow && (
          <IconButton
            icon={{ Component: ArrowSq, orientation: 'right' }}
            title={name}
            testId="trail-list-item-arrow-button"
            variant={arrowVariant}
            className={styles.arrowButton}
          />
        )}
      </a>
    </li>
  );
};

export default TrailListItem;
